import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, Check, X, Loader2 } from "lucide-react";
import { getStoreItems, purchaseItem } from "../api/store";
import { Item, RootState } from "../redux/type";
import { useSelector, useDispatch } from "react-redux";
import { setUserData } from "../redux/actions";
import toast from "react-hot-toast";

// --- Styled Components ---

const StoreContainer = styled.div`
  padding: 1.5rem 1rem;
  max-width: 800px;
  margin: 0 auto 100px;
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 2rem;
`;

const StoreTitle = styled.h1`
  font-size: 2.2rem;
  font-weight: 800;
  color: white;
  margin: 0;
  background: linear-gradient(90deg, #ccff00, #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const UserBalance = styled.div`
  background: rgba(255, 255, 255, 0.05);
  color: #ccff00;
  padding: 0.5rem 1rem;
  border-radius: 99px;
  font-size: 1rem;
  font-weight: 600;
  border: 1px solid rgba(204, 255, 0, 0.3);
  display: inline-block;
  margin-top: 0.75rem;
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 2rem;
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  color: ${(props) => (props.active ? "#fff" : "rgba(255, 255, 255, 0.5)")};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;

  &:after {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: #ccff00;
    transform: ${(props) => (props.active ? "scaleX(1)" : "scaleX(0)")};
    transition: transform 0.3s ease-out;
  }
`;

const ItemsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
`;

const ItemCard = styled(motion.div)<{ owned?: boolean }>`
  background: #1e1e1e;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  position: relative;
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
  }
`;

const ItemImage = styled.div<{ imageUrl: string }>`
  height: 120px;
  background-image: url(${(props) => props.imageUrl});
  background-size: cover;
  background-position: center;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(30, 30, 30, 0.8), transparent);
  }
`;

const ItemContent = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const OwnedBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 255, 157, 0.2);
  color: #00ff9d;
  padding: 0.25rem 0.6rem;
  border-radius: 99px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid rgba(0, 255, 157, 0.4);
`;

const ItemName = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  margin: 0 0 0.5rem;
`;

const ItemDescription = styled.p`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 1rem;
  flex-grow: 1;
`;

const PurchaseButton = styled.button`
  padding: 0.75rem;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #ccff00, #3b82f6);
  color: #111;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(110, 231, 183, 0.3);
  }

  &:disabled {
    background: #333;
    color: #777;
    cursor: not-allowed;
  }
`;

// --- Modal Components ---

const ModalBackdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: #1e1e1e;
  padding: 2rem;
  border-radius: 16px;
  width: 90%;
  max-width: 400px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: #ccff00;
  margin: 0 0 1rem;
  text-align: center;
`;

const ModalText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 1.5rem;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

// --- Main Component ---

const Store = () => {
  const user = useSelector((state: RootState) => state.userData);
  const dispatch = useDispatch();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All");

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    item: Item | null;
  }>({ isOpen: false, item: null });
  const [purchaseLoading, setPurchaseLoading] = useState(false);

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      const storeItems = await getStoreItems();
      setItems(storeItems);
      setLoading(false);
    };
    fetchItems();
  }, []);

  const handlePurchaseConfirm = async () => {
    if (!confirmModal.item) return;

    setPurchaseLoading(true);
    const purchaseToast = toast.loading("Processing purchase...");

    try {
      const result = await purchaseItem(user.uid, confirmModal.item._id);
      dispatch(setUserData(result.user));
      toast.success(`You purchased ${confirmModal.item.name}!`, {
        id: purchaseToast,
      });
      setConfirmModal({ isOpen: false, item: null });
    } catch (error: any) {
      toast.error(error.message, { id: purchaseToast });
    } finally {
      setPurchaseLoading(false);
    }
  };

  const userOwnedItemIds = new Set(
    user.inventory?.map((item) => item._id) || []
  );

  const filteredItems = items.filter((item) => {
    if (activeTab === "All") return true;
    // This assumes your Item model has a 'type' field, e.g., "Sticker" or "Badge"
    // @ts-ignore
    return true;
  });

  if (loading)
    return (
      <StoreContainer>
        <StoreTitle>Loading Store...</StoreTitle>
      </StoreContainer>
    );

  return (
    <>
      <StoreContainer>
        <Header initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <StoreTitle>Store</StoreTitle>
          <UserBalance>{user.points.toLocaleString()} XP</UserBalance>
        </Header>

        <TabsContainer>
          <TabButton
            active={activeTab === "All"}
            onClick={() => setActiveTab("All")}
          >
            All
          </TabButton>
          <TabButton
            active={activeTab === "Stickers"}
            onClick={() => setActiveTab("Stickers")}
          >
            Stickers
          </TabButton>
        </TabsContainer>

        <ItemsGrid>
          {filteredItems.map((item, index) => {
            const isOwned = userOwnedItemIds.has(item._id);
            const canAfford = user.points >= item.price;
            return (
              <ItemCard
                key={item._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <ItemImage imageUrl={item.image}>
                  {isOwned && <OwnedBadge>Owned</OwnedBadge>}
                </ItemImage>
                <ItemContent>
                  <ItemName>{item.name}</ItemName>
                  <ItemDescription>{item.description}</ItemDescription>
                  <PurchaseButton
                    onClick={() => setConfirmModal({ isOpen: true, item })}
                    disabled={!canAfford || isOwned}
                  >
                    <ShoppingCart size={16} />
                    {isOwned
                      ? "Owned"
                      : !canAfford
                      ? "Not Enough XP"
                      : `${item.price.toLocaleString()} XP`}
                  </PurchaseButton>
                </ItemContent>
              </ItemCard>
            );
          })}
        </ItemsGrid>
      </StoreContainer>

      <AnimatePresence>
        {confirmModal.isOpen && (
          <ModalBackdrop
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() =>
              !purchaseLoading && setConfirmModal({ isOpen: false, item: null })
            }
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalTitle>Confirm Purchase</ModalTitle>
              <ModalText>
                Are you sure you want to buy{" "}
                <strong>{confirmModal.item?.name}</strong> for{" "}
                <strong>{confirmModal.item?.price.toLocaleString()} XP</strong>?
              </ModalText>
              <ModalButtons>
                <button
                  onClick={() => setConfirmModal({ isOpen: false, item: null })}
                  style={{
                    padding: "10px 20px",
                    background: "#555",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                    cursor: "pointer",
                  }}
                  disabled={purchaseLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePurchaseConfirm}
                  style={{
                    padding: "10px 20px",
                    background: "#ccff00",
                    border: "none",
                    borderRadius: "8px",
                    color: "#111",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  disabled={purchaseLoading}
                >
                  {purchaseLoading ? (
                    <>
                      <Loader2
                        size={18}
                        style={{ animation: "spin 1s linear infinite" }}
                      />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      Confirm
                    </>
                  )}
                </button>
              </ModalButtons>
            </ModalContent>
          </ModalBackdrop>
        )}
      </AnimatePresence>
    </>
  );
};

export default Store;
